import React, { useEffect, useMemo, useCallback } from 'react';
import { cn, Button, formatMessageStreaming } from '@ideoz/ui';
import type { IdeozMessageProps, StreamingAnimatedContentProps } from '../../types/IdeozMessage.types';
import '../atoms/ideoz-message-animations.scss';

// Streaming-aware animated markdown content component
const StreamingAnimatedContent: React.FC<StreamingAnimatedContentProps> =
  React.memo(({ content, isStreaming = false }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const observerRef = React.useRef<MutationObserver | null>(null);
    // Custom hook to handle element animations as they appear
    useEffect(() => {
      if (!containerRef.current) return;

      const container = containerRef.current;

      // Function to animate new elements
      const animateNewElements = () => {
        const elements = container.querySelectorAll(':scope > *:not([data-animated])');
        elements.forEach((element, index) => {
          const htmlElement = element as HTMLElement;

          // Mark as animated to prevent re-animation
          htmlElement.setAttribute('data-animated', 'true');

          // Set initial state
          htmlElement.style.opacity = '0';
          htmlElement.style.transform = 'translateY(8px)';
          htmlElement.style.transition = 'opacity 0.4s ease-out, transform 0.3s ease-out';

          // Animate in with a slight delay for staggered effect
          const delay = isStreaming ? index * 50 : 0;
          setTimeout(() => {
            requestAnimationFrame(() => {
              htmlElement.style.opacity = '1';
              htmlElement.style.transform = 'translateY(0)';
            });
          }, delay);
        });
      };

      // Initial animation of existing content
      animateNewElements();

      // Set up observer for streaming content
      if (isStreaming) {
        observerRef.current = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              // Small delay to ensure the DOM is stable
              setTimeout(animateNewElements, 10);
            }
          });
        });

        observerRef.current.observe(container, {
          childList: true,
          subtree: true,
        });
      }

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, [content, isStreaming]);

    return (
      <div
        ref={containerRef}
        className="streaming-markdown-content"
        style={{
          // Ensure smooth rendering performance
          contain: 'layout style paint',
          willChange: isStreaming ? 'contents' : 'auto',
        }}
      >
        {content}
      </div>
    );
  });

const IdeozMessage: React.FC<IdeozMessageProps> = ({
  children,
  className,
  isReflection = false,
  isRepeated = false,
  isStreaming = false,
  isRtl: propIsRtl,
  onActionClick = () => {},
}) => {
  // Convert children to string for markdown processing
  const messageContent = typeof children === 'string' ? children : String(children);
  const cleanedMessage = messageContent;

  // Memoize the formatted content to prevent unnecessary re-renders
  // Use streaming-safe formatting when content is being streamed
  const formattedResult = useMemo(() => {
    const result = formatMessageStreaming(cleanedMessage);
    return result;
  }, [cleanedMessage]);

  const { safeContent: formattedContent, isRtl: detectedIsRtl } = formattedResult;

  // Use prop isRtl if provided, otherwise use detected from content
  const isRtl = propIsRtl !== undefined ? propIsRtl : detectedIsRtl;

  // Memoize the action handler to prevent unnecessary re-renders
  const handleActionClick = useCallback(
    (action: string) => {
      onActionClick(action);
    },
    [onActionClick]
  );

  const handleIsRepeatedButtonClick = () => {
    handleActionClick(
      isRtl
        ? isRepeated
          ? 'أعد صياغتها'
          : 'تراجع وتأمل'
        : isRepeated
          ? 'Reframe it'
          : 'Step back and reflect '
    );
  };

  const handleSkipButtonClick = () => {
    handleActionClick(isRtl ? 'تخطي' : 'Skip');
  };

  return (
    <>
      {isReflection || isRepeated ? (
        <div
          data-testid={isReflection ? 'ai-message-reflection' : 'ai-message-repeated'}
          className={cn(
            // Layout: flex with icon and text
            isRtl ? 'flex items-end gap-2' : 'flex items-start gap-2',
            className
          )}
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          <span>
            <svg
              id="LogoAiOutline"
              width={'20'}
              height={'20'}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="none"
            >
              <rect
                x="0.267133"
                y="0.267133"
                width="19.4657"
                height="19.4657"
                rx="9.73287"
                stroke="#98A2B3"
                strokeWidth="0.534267"
              />
              <path
                d="M10.6158 13.8213C10.283 13.8213 9.99972 13.7789 9.76604 13.6939C9.53236 13.6019 9.34116 13.4533 9.19245 13.2481C9.04375 13.0358 8.93399 12.7492 8.86318 12.3882C8.79944 12.0273 8.76758 11.5779 8.76758 11.04C8.76758 10.5022 8.79944 10.0528 8.86318 9.69186C8.92691 9.33093 9.02959 9.04431 9.17121 8.83199C9.31992 8.61968 9.50757 8.46753 9.73417 8.37552C9.96785 8.28352 10.2511 8.23752 10.5839 8.23752C10.8176 8.23752 11.0336 8.25875 11.2319 8.30121C11.4372 8.3366 11.6178 8.40383 11.7736 8.50291C11.9364 8.59491 12.071 8.73292 12.1772 8.91692H12.2622V6.25781H13.6643V13.7364H12.3472L12.3047 13.0676H12.2303C12.1241 13.2799 11.9825 13.4392 11.8054 13.5453C11.6355 13.6515 11.4478 13.7223 11.2425 13.7576C11.0371 13.8001 10.8282 13.8213 10.6158 13.8213ZM11.2212 12.6112C11.4832 12.6112 11.6815 12.5793 11.8161 12.5156C11.9577 12.4448 12.0604 12.3351 12.1241 12.1865C12.1808 12.045 12.2162 11.8787 12.2303 11.6876C12.2516 11.4965 12.2622 11.2771 12.2622 11.0294C12.2622 10.7747 12.2516 10.5553 12.2303 10.3713C12.2162 10.1873 12.1808 10.0316 12.1241 9.90417C12.0604 9.7414 11.9577 9.62817 11.8161 9.56447C11.6815 9.4937 11.4832 9.45832 11.2212 9.45832C10.9876 9.45832 10.7999 9.47601 10.6583 9.51139C10.5167 9.5397 10.4104 9.60693 10.3396 9.71309C10.2759 9.81217 10.2334 9.96786 10.2122 10.1802C10.1909 10.3854 10.1803 10.6685 10.1803 11.0294C10.1803 11.3904 10.1909 11.677 10.2122 11.8893C10.2334 12.1016 10.2759 12.2608 10.3396 12.367C10.4104 12.4661 10.5167 12.5333 10.6583 12.5687C10.7999 12.597 10.9876 12.6112 11.2212 12.6112Z"
                fill="black"
              />
              <rect x="6.35352" y="6.25781" width="1.41353" height="1.41353" fill="black" />
              <rect x="6.35352" y="8.31787" width="1.41353" height="5.40912" fill="black" />
            </svg>
          </span>
          <div
            className={cn(
              // Base text styling with 12px font
              'border border-blue-gray-100 rounded-lg bg-blue-gray-50 text-primary-900 text-base font-normal',

              // Layout
              'flex flex-col p-5 justify-center items-start gap-4'
            )}
          >
            {/* Commented out reflection content */}
            {/* <div><h3 className="font-semibold mb-2">
              {isRepeated? "I see you repeat the prompt, want help reframing this":"mmm..., before we dive in, want to sharpen your lens?"} </h3> 
              <p className='text-gray-800'>{isRepeated?"can help you reframe this problem so you can understand it more clearly": "Let's take a moment to reflect before diving in. What's shaping your view?"}</p>
            </div> */}

            <div>
              <StreamingAnimatedContent content={formattedContent} isStreaming={isStreaming} />
            </div>

            <div className="flex gap-2">
              <Button 
                data-testid={isRepeated ? 'btn-reframe' : 'btn-reflect'}
                variant="primary" 
                onClick={handleIsRepeatedButtonClick}
              >
                {isRtl
                  ? isRepeated
                    ? 'أعد صياغتها'
                    : 'تراجع وتأمل'
                  : isRepeated
                    ? 'Reframe it'
                    : 'Step back and reflect '}
              </Button>
              <Button 
                data-testid="btn-skip"
                variant="secondary-gray" 
                onClick={handleSkipButtonClick}
              >
                {isRtl ? 'تخطي' : 'Skip'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            // Layout: flex with icon and text
            'relative mb-3 flex items-start gap-2',
            className
          )}
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {/* AI Icon */}
          <span>
            <svg
              id="LogoAiOutline"
              width={'20'}
              height={'20'}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="none"
            >
              <rect
                x="0.267133"
                y="0.267133"
                width="19.4657"
                height="19.4657"
                rx="9.73287"
                stroke="#98A2B3"
                strokeWidth="0.534267"
              />
              <path
                d="M10.6158 13.8213C10.283 13.8213 9.99972 13.7789 9.76604 13.6939C9.53236 13.6019 9.34116 13.4533 9.19245 13.2481C9.04375 13.0358 8.93399 12.7492 8.86318 12.3882C8.79944 12.0273 8.76758 11.5779 8.76758 11.04C8.76758 10.5022 8.79944 10.0528 8.86318 9.69186C8.92691 9.33093 9.02959 9.04431 9.17121 8.83199C9.31992 8.61968 9.50757 8.46753 9.73417 8.37552C9.96785 8.28352 10.2511 8.23752 10.5839 8.23752C10.8176 8.23752 11.0336 8.25875 11.2319 8.30121C11.4372 8.3366 11.6178 8.40383 11.7736 8.50291C11.9364 8.59491 12.071 8.73292 12.1772 8.91692H12.2622V6.25781H13.6643V13.7364H12.3472L12.3047 13.0676H12.2303C12.1241 13.2799 11.9825 13.4392 11.8054 13.5453C11.6355 13.6515 11.4478 13.7223 11.2425 13.7576C11.0371 13.8001 10.8282 13.8213 10.6158 13.8213ZM11.2212 12.6112C11.4832 12.6112 11.6815 12.5793 11.8161 12.5156C11.9577 12.4448 12.0604 12.3351 12.1241 12.1865C12.1808 12.045 12.2162 11.8787 12.2303 11.6876C12.2516 11.4965 12.2622 11.2771 12.2622 11.0294C12.2622 10.7747 12.2516 10.5553 12.2303 10.3713C12.2162 10.1873 12.1808 10.0316 12.1241 9.90417C12.0604 9.7414 11.9577 9.62817 11.8161 9.56447C11.6815 9.4937 11.4832 9.45832 11.2212 9.45832C10.9876 9.45832 10.7999 9.47601 10.6583 9.51139C10.5167 9.5397 10.4104 9.60693 10.3396 9.71309C10.2759 9.81217 10.2334 9.96786 10.2122 10.1802C10.1909 10.3854 10.1803 10.6685 10.1803 11.0294C10.1803 11.3904 10.1909 11.677 10.2122 11.8893C10.2334 12.1016 10.2759 12.2608 10.3396 12.367C10.4104 12.4661 10.5167 12.5333 10.6583 12.5687C10.7999 12.597 10.9876 12.6112 11.2212 12.6112Z"
                fill="black"
              />
              <rect x="6.35352" y="6.25781" width="1.41353" height="1.41353" fill="black" />
              <rect x="6.35352" y="8.31787" width="1.41353" height="5.40912" fill="black" />
            </svg>
          </span>

          {/* Message text with smooth animated markdown formatting */}
          <div className="max-w-[95%]">
            <StreamingAnimatedContent content={formattedContent} isStreaming={isStreaming} />
          </div>
        </div>
      )}
    </>
  );
};

export default IdeozMessage;
